import { DayGroup, EmptyDay, MeetingRow, useNow } from '@wundo/ui';
import type { CalendarMeetingItem } from '@/lib/meetings.ts';
import { isSameDay } from '@/lib/week.ts';
import { m } from '@/paraglide/messages.js';
import {
  formatDayAndMonth,
  formatDuration,
  formatTime,
  formatWeekdayDayAndMonth,
  formatWeekdayLong,
  meetingTitle,
} from './meetingFormat.ts';
import {
  followedJourneySuggestion,
  meetingCardLabel,
  meetingState,
} from './meetingCardState.ts';
import { AgendaMeetingPill } from './AgendaMeetingPill.tsx';
import { AgendaJourneyLinkPill } from './AgendaJourneyLinkPill.tsx';

export interface AgendaListViewProps {
  days: Date[];
  meetings: CalendarMeetingItem[];
  /* The click: open the conversation, or add the meeting to Wundo. */
  onSelect: (meeting: CalendarMeetingItem) => void;
  /* The suggested journey step's own pill, on a card whose click opens the meeting. */
  onLink: (meeting: CalendarMeetingItem) => void;
}

export function AgendaListView({
  days,
  meetings,
  onSelect,
  onLink,
}: AgendaListViewProps) {
  const today = useNow();

  return (
    <>
      {days.map((day) => {
        const dayMeetings = meetings.filter((meeting) =>
          isSameDay(new Date(meeting.starts_at), day),
        );
        const isToday = isSameDay(day, today);

        return (
          <DayGroup
            key={day.toISOString()}
            day={isToday ? m.agenda_today() : formatWeekdayLong(day)}
            /* Today's heading gave up the weekday, so the date says it. */
            date={isToday ? formatWeekdayDayAndMonth(day) : formatDayAndMonth(day)}
            today={isToday}
          >
            {dayMeetings.map((meeting) => {
              const state = meetingState(meeting);
              const suggestion = followedJourneySuggestion(meeting);

              return (
                <MeetingRow
                  key={meeting.id}
                  title={meetingTitle(meeting)}
                  time={formatTime(meeting.starts_at)}
                  duration={formatDuration(meeting)}
                  state={state}
                  aria-label={meetingCardLabel(meeting)}
                  onClick={() => onSelect(meeting)}
                  badge={
                    state !== 'inert' && (
                      <AgendaMeetingPill meeting={meeting} state={state} />
                    )
                  }
                  action={
                    suggestion && (
                      <AgendaJourneyLinkPill
                        suggestion={suggestion}
                        onLink={() => onLink(meeting)}
                      />
                    )
                  }
                />
              );
            })}
            {dayMeetings.length === 0 && <EmptyDay>{m.agenda_empty_day()}</EmptyDay>}
          </DayGroup>
        );
      })}
    </>
  );
}
